import { Button } from '@/components/ui/button';
import {
  getAuth,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  ConfirmationResult,
} from 'firebase/auth';
import { PatternFormat } from 'react-number-format';
import { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@radix-ui/react-label';
import { Loader } from 'src/components/Loader';
import { MainLayout } from 'src/pages/MainLayout';
import { Header } from 'src/components/Header';
import { useNavigate, useLocation } from 'react-router-dom';
import { KeyboardEvent } from 'react';
import { cn } from '@/lib/utils';
import {
  handlePhoneInputChange,
  isValidPhoneLength,
  PHONE_INPUT_FORMAT,
  PHONE_INPUT_PLACEHOLDER,
} from '@libs/schemas';

export const SignInPage = () => {
  const [phone, setPhone] = useState<string | number>('');
  const [phoneFormatted, setPhoneFormatted] = useState<string | number>('');
  const [loading, setLoading] = useState(false);
  const [recaptchaVerifier, setRecaptchaVerifier] = useState<RecaptchaVerifier>();
  const [confirmationResults, setConfirmationResults] = useState<ConfirmationResult>();
  const [confirmationCode, setConfirmationCode] = useState<string>('');
  const [confirmationError, setConfirmationError] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';

  useEffect(() => {
    const auth = getAuth();
    setRecaptchaVerifier(new RecaptchaVerifier(auth, 'recaptcha-container', { size: 'invisible' }));
  }, []);

  const onConfirm = async () => {
    setLoading(true);
    try {
      setConfirmationResults(
        await signInWithPhoneNumber(getAuth(), phone.toString(), recaptchaVerifier!),
      );
    } catch (e) {
      setLoading(false);
    }
  };

  const handleKeyDown = async (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter') {
      await onVerify();
    }
  };

  const onVerify = async () => {
    try {
      const res = await confirmationResults?.confirm(confirmationCode);

      if (res?.user) {
        const tokens = await res.user.getIdTokenResult();
        const userRoles = tokens.claims.roles as string[];
        if (userRoles && userRoles.includes('admin')) {
          navigate(from, { replace: true });
        } else {
          setErrorMessage(
            'You are not authorized to access this site. Please contact admin for access.',
          );
        }
      }
    } catch (e) {
      setConfirmationError('Invalid code');
    }
  };

  const confirmation = (
    <MainLayout>
      <Header />
      <div className="flex flex-col w-screen h-screen justify-center items-center p-[24px]">
        <div className="flex flex-col w-full justify-center items-center">
          <h4 className="text-text-primary text-[24px] mb-6 font-bold">
            Verify your mobile number
          </h4>
          {!!confirmationError && <p className="text-red-700">{confirmationError}</p>}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="code" className="text-text-light mb-1 font-light">
              Verification Code
            </Label>
            <Input
              id="code"
              placeholder="Enter Verification Code"
              value={confirmationCode}
              onChange={(e) => setConfirmationCode(e.target.value.replace(/\D/g, ''))}
              className="mb-1 w-[450px]"
            />
            {errorMessage && <p className="text-red-700 text-xs mt-1">{errorMessage}</p>}
          </div>
          <p className="text-text-light mb-4 text-[14px]">
            We texted a one-time verification code to {phoneFormatted}
          </p>
        </div>
        <div className="flex flex-col w-full justify-center items-center mt-6">
          <Button
            onClick={() => onConfirm()}
            className="bg-secondary text-text-light hover:text-white w-[450px]"
          >
            Resend Code
          </Button>
          <Button
            className="text-white bg-primary w-[450px] mt-4"
            disabled={confirmationCode.length !== 6}
            onKeyDown={(e) => handleKeyDown(e)}
            onClick={() => onVerify()}
          >
            Confirm
          </Button>
        </div>
      </div>
    </MainLayout>
  );

  if (confirmationResults) {
    return confirmation; //Did this to test the confirmation code screen
  }

  const content = (
    <MainLayout>
      <Header hideBackButton />
      <div className="flex flex-col w-screen h-screen justify-center items-center p-[24px]">
        <div className="flex flex-col justify-evenly">
          <h4 className="text-text-primary text-[24px] mb-6 font-bold">
            Welcome to the admin dashboard
          </h4>
          <div className="w-[450px]">
            <label className="text-text-light mb-1 text-[14px] block w-full">Mobile Number</label>
            <PatternFormat
              placeholder={PHONE_INPUT_PLACEHOLDER}
              onValueChange={(e) => {
                handlePhoneInputChange(e, (phone, formatted) => {
                  setPhone(phone);
                  setPhoneFormatted(formatted);
                });
              }}
              className={cn(
                'flex h-10 w-full mb-3 mt-2 text-text-light rounded-md border border-input border-disabled bg-white  px-3 py-2 text-md ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
              )}
              format={PHONE_INPUT_FORMAT}
            />
          </div>
          <p className="text-text-light text-[14px] mb-6">
            We'll send you a verification code by SMS. Standard rates may apply.
          </p>

          <Button
            className="bg-primary text-white w-[450px]"
            disabled={!isValidPhoneLength(phone?.toString() || '') || loading}
            onClick={onConfirm}
          >
            Continue
          </Button>
        </div>
      </div>
    </MainLayout>
  );

  return (
    <>
      {loading ? (
        <MainLayout>
          <Loader title="Sending verification code" />
        </MainLayout>
      ) : (
        content
      )}
      <div id="recaptcha-container"></div>
    </>
  );
};
